import { Box, Typography, Button } from "@mui/joy";
import { TopCards, ManageProject, Analytics, ProjectSummary, CalendarWidget, UpcomingEvents, Assistance } from "./components";
import { useAuth } from "../../hooks";
import moment from "moment";
import { Plus, Search, Globe, Moon, Bell, Target, BarChartBig, ShoppingCart, Layers } from "lucide-react";
import { Divider } from "@mui/joy";

export default function Dashboard() {
    const { user } = useAuth();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                bgcolor: "background.body",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    overflow: "auto",
                    p: 4,
                    gap: 4,
                }}
            >
                {/* Left Column: Stats & Recent Activity */}
                <Box sx={{ width: 300, display: "flex", flexDirection: "column", gap: 4 }}>
                    <Box>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 0.5 }}>
                            {moment().format("dddd, MMM DD, YYYY")}
                        </Typography>
                        <Typography level="h3" sx={{ fontWeight: 800 }}>
                            Good morning, Captain!
                        </Typography>
                        <Divider sx={{ my: 2, width: "100%" }} />

                        <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 2, fontWeight: 700 }}>
                            UPDATES FROM YESTERDAY
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: "lg",
                                        bgcolor: "primary.softBg",
                                        color: "primary.500",
                                    }}
                                >
                                    <Target size={20} />
                                </Box>
                                <Box>
                                    <Typography level="title-lg" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                        2,110
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        Site Visits
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: "lg",
                                        bgcolor: "success.softBg",
                                        color: "success.500",
                                    }}
                                >
                                    <BarChartBig size={20} />
                                </Box>
                                <Box>
                                    <Typography level="title-lg" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                        $8.2M
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        Revenue
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: "lg",
                                        bgcolor: "warning.softBg",
                                        color: "warning.500",
                                    }}
                                >
                                    <ShoppingCart size={20} />
                                </Box>
                                <Box>
                                    <Typography level="title-lg" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                        1,124
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        Active Projects
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 2, fontWeight: 700 }}>
                            ACTIVE DISPATCHES
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {[
                                { name: "GAF Timberline HDZ", qty: 42, type: "Delivery" },
                                { name: "Owens Corning Duration", qty: 28, type: "Stocking" },
                                { name: "Ridge Cap Profile", qty: 15, type: "Pickup" },
                                { name: "Starter Shingle Bundle", qty: 12, type: "Delivery" },
                            ].map((item, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: "lg",
                                        bgcolor: "background.level1",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "md",
                                            bgcolor: "neutral.200",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Layers size={20} color="var(--joy-palette-neutral-500)" />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography level="title-sm" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                                            {item.name}
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                            {item.qty} Sq · {item.type}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                            <Button
                                variant="plain"
                                color="primary"
                                size="sm"
                                sx={{ mt: 1, alignSelf: "flex-end", fontWeight: 700 }}
                            >
                                All dispatches →
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Center Column: Charts */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                    <Box sx={{ display: "flex", gap: 4, height: 320 }}>
                        <Box sx={{ flex: 1 }}>
                            <Analytics />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <ProjectSummary />
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <ManageProject />
                    </Box>
                </Box>

                {/* Right Column: Help & Calendar */}
                <Box sx={{ width: 340, display: "flex", flexDirection: "column", gap: 4 }}>
                    <Assistance />
                    <UpcomingEvents />
                </Box>
            </Box>
        </Box>
    );
}

const greeting = (() => {
    const h = moment().hour();
    return h < 5
        ? "Good Night"
        : h < 12
          ? "Good Morning"
          : h < 17
            ? "Good Afternoon"
            : h < 21
              ? "Good Evening"
              : "Good Night";
})();
